import React from 'react';
import { View, Text, StyleSheet, type StyleProp, type ViewStyle } from 'react-native';
import { Image } from 'expo-image';
import { ShieldCheck } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { useActiveBrand } from '../../hooks/useActiveBrand';
import { useLogoAspect } from '../../hooks/useLogoAspect';
import { resolveBrandLogoUrl } from '../../store/brandStore';

const MARK_SIZE = 28;
// Wordmark plate: logos are wide, so they get a row of their own above the
// program line. The mark fills the plate's width (176 = the sidebar tile's
// inner width minus the plate's own padding) and takes whatever height its
// aspect gives, clamped: a very wide wordmark bottoms out at 24px, a
// compact lockup with small text tops out at 40px so the text stays legible.
const LOGO_MIN_HEIGHT = 24;
const LOGO_MAX_HEIGHT = 40;
const LOGO_MAX_WIDTH = 176;

const PROGRAM_LINE = 'Wildfire readiness program';

type BrandTileProps = {
  /** 'sidebar' (default): the insurer name leads. 'welcome': "Shared with
   *  you by {name}" leads, for the map's first-visit overlay. 'rail': the
   *  map screen's 64px collapsed sidebar — logo mark only, no text, same
   *  testID so the insurer-link audit still finds it there. */
  variant?: 'sidebar' | 'welcome' | 'rail';
  style?: StyleProp<ViewStyle>;
};

/**
 * Insurer identity card for the branded insurer-link journey. Two shapes:
 * with a logo, the insurer's wordmark on a white plate above the program
 * line (the mark carries the name, so it is not repeated); without one, a
 * shield glyph in the accent beside the name. Renders null with no active
 * brand — mount it unconditionally, it decides for itself, same as
 * BrandStrip (see that file for why the sidebar header keeps the wordmark
 * separately rather than folding this in).
 */
export function BrandTile({ variant = 'sidebar', style }: BrandTileProps) {
  const { colors } = useTheme();
  const brand = useActiveBrand();
  const logoUrl = brand?.logoUrl ? resolveBrandLogoUrl(brand.logoUrl) : '';
  const { aspect, onLoad } = useLogoAspect(logoUrl);

  if (!brand) return null;

  const fill = { backgroundColor: colors.primarySoft, borderColor: colors.border };
  const leadLine = variant === 'welcome' ? `Shared with you by ${brand.name}` : brand.name;
  const a11yLabel = `${leadLine}, ${PROGRAM_LINE}`;

  if (variant === 'rail') {
    if (logoUrl) {
      return (
        <View
          testID="brand-tile"
          accessibilityLabel={a11yLabel}
          style={[
            styles.railPlate,
            { backgroundColor: colors.logoPlate, borderColor: colors.border },
            style,
          ]}>
          <Image
            source={{ uri: logoUrl }}
            contentFit="contain"
            onLoad={onLoad}
            accessibilityLabel={brand.name}
            style={styles.railLogo}
          />
        </View>
      );
    }
    return (
      <View
        testID="brand-tile"
        accessibilityLabel={a11yLabel}
        style={[styles.railMark, { backgroundColor: colors.primarySoft }, style]}>
        <ShieldCheck size={16} color={colors.primary} strokeWidth={2} />
      </View>
    );
  }

  if (logoUrl) {
    const logoHeight = Math.round(
      Math.max(LOGO_MIN_HEIGHT, Math.min(LOGO_MAX_HEIGHT, LOGO_MAX_WIDTH / aspect))
    );
    const logoWidth = Math.min(LOGO_MAX_WIDTH, Math.round(logoHeight * aspect));
    return (
      <View testID="brand-tile" accessibilityLabel={a11yLabel} style={[styles.stack, fill, style]}>
        {variant === 'welcome' ? (
          <Text style={[TYPOGRAPHY.caption, { color: colors.textSecondary }]}>
            Shared with you by
          </Text>
        ) : null}
        {/* Partner logos are drawn for white and are not ours to recolour, so
            the plate stays white in both themes (colors.logoPlate). Fixed
            height and a cached aspect keep the row from shifting on load. */}
        <View
          style={[styles.plate, { backgroundColor: colors.logoPlate, borderColor: colors.border }]}>
          <Image
            source={{ uri: logoUrl }}
            contentFit="contain"
            onLoad={onLoad}
            accessibilityLabel={brand.name}
            style={{ width: logoWidth, height: logoHeight }}
          />
        </View>
        <Text style={[TYPOGRAPHY.caption, { color: colors.textSecondary }]} numberOfLines={1}>
          {PROGRAM_LINE}
        </Text>
      </View>
    );
  }

  return (
    <View testID="brand-tile" accessibilityLabel={a11yLabel} style={[styles.row, fill, style]}>
      <View style={[styles.mark, { backgroundColor: colors.primarySoft }]}>
        <ShieldCheck size={18} color={colors.primary} strokeWidth={2} />
      </View>
      <View style={styles.textColumn}>
        <Text style={[TYPOGRAPHY.label, { color: colors.text }]} numberOfLines={1}>
          {leadLine}
        </Text>
        <Text style={[TYPOGRAPHY.caption, { color: colors.textSecondary }]} numberOfLines={1}>
          {PROGRAM_LINE}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd,
    paddingVertical: SPACING.smd,
    paddingHorizontal: SPACING.smd,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: BORDER_RADIUS.md,
  },
  stack: {
    alignItems: 'flex-start',
    gap: SPACING.xs,
    paddingVertical: SPACING.smd,
    paddingHorizontal: SPACING.smd,
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: BORDER_RADIUS.md,
  },
  plate: {
    paddingVertical: SPACING.xs,
    paddingHorizontal: SPACING.sm,
    borderRadius: BORDER_RADIUS.sm,
    borderWidth: StyleSheet.hairlineWidth,
  },
  mark: {
    width: MARK_SIZE,
    height: MARK_SIZE,
    borderRadius: BORDER_RADIUS.sm,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  textColumn: {
    flex: 1,
    flexShrink: 1,
  },
  railPlate: {
    width: 36,
    height: 36,
    borderRadius: BORDER_RADIUS.sm,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
  },
  railLogo: {
    width: 28,
    height: 28,
  },
  railMark: {
    width: 32,
    height: 32,
    borderRadius: BORDER_RADIUS.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
